import { DateTime } from "luxon";
import { useConfig } from "../config/configContext";
import { Room, Session } from "../config/parsedConfig";
import { useNow } from "./nowContext";

export type CurrentSessionInfo = {
    currentSession: Session | null;
    upcomingSession: Session;
    upcomingSessionIsTomorrow: boolean;
    currentSessionRunningTime: number;
    currentSessionTimeLeft: number;
    timeUntilUpcomingSession: number;
    currentDateInTimezone: string;
}

/**
 * Returns the current or upcoming session of the given room.
 * This hook makes use of ``useNow()``.
 */
export const useCurrentSession: (room: Room) => CurrentSessionInfo
 = (room: Room) => {
    const now = useNow();

    // find the current or and upcoming session:
    // iterate over the sessions of the room, until we find one that is
    // (A) currently running (-> current) or 
    // (B) is already over (-> next one is upcoming. if there is no next one, the upcoming one is the first one (of the next day))
    let currentSession = null;
    let upcomingSession = room.schedule[0]; //default, in case none has started yet
    let upcomingSessionIsTomorrow = false;
    for (let i=0; i<room.schedule.length; i++) {
        const session = room.schedule[i];
        if (session.startTime <= now) {
            upcomingSession = room.schedule[i+1];
            if (session.endTime >= now) {
                currentSession = session;
                break;
            }
        }
    }
    if (!upcomingSession) {
        upcomingSession = room.schedule[0];
        upcomingSessionIsTomorrow = true;
    }

    const currentSessionRunningTime = currentSession ? now - currentSession.startTime : 0;
    const currentSessionTimeLeft = currentSession ? currentSession.endTime - now : 0;
    const timeUntilUpcomingSession = upcomingSession.startTime - now + (upcomingSessionIsTomorrow ? 24*3600 : 0);

    const {timezone} = useConfig();
    const currentDateInTimezone = DateTime.now().setZone(timezone).toISODate() || "";

    return {
        currentSession,
        upcomingSession,
        upcomingSessionIsTomorrow,
        currentSessionRunningTime,
        currentSessionTimeLeft,
        timeUntilUpcomingSession,
        currentDateInTimezone
    };
}